import { FloatingFocusManager, FloatingPortal, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react'
import { IconX } from '@tabler/icons-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ReactNode } from 'react'

import { ScrollFades, useScrollFade } from '../../hooks/useScrollFade'
import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { AstraHeading } from '../Typography/AstraHeading/AstraHeading'
import { astraTwMerge } from '../Utilities/astraTwMerge'

type Alignment = 'top' | 'center'

export type AstraDialogProps = {
    children: ReactNode
    className?: string
    isOpen: boolean
    /**
     * If given, renders in a bordered header bar instead of the title (and potentially afterTitle).
     * The title is then only used for accessibility purposes.
     */
    header?: ReactNode
    title: string
    /**
     * If false, hides the header bar (close button, title, afterTitle).
     * The title is still used for accessibility (sr-only heading).
     */
    showHeader?: boolean
    /**
     * Overlay is always rendered, showOverlay just decides whether
     * make it visible or not
     */
    showOverlay?: boolean
    noPadding?: boolean
    afterTitle?: ReactNode
    onCloseDialog?: () => void
    /**
     * If true, the dialog will be modal and prevent interactions with the rest of the page
     *  THIS WILL ALSO BREAK SOME KEYBOARD ACCESSIBILITY and form inputs
     */
    isModal?: boolean
    align?: Alignment
    'data-test'?: string
    footer?: ReactNode
    disableOutsideClick?: boolean
    /**
     * If true, disables overflow scrolling on the content area.
     * Useful when dialog contains absolutely positioned elements (like Select dropdowns) that need to escape the content area.
     */
    disableOverflow?: boolean
}

// Generic dialog component that can be used to display any content in a dialog
export const AstraDialog = ({
    children,
    disableOutsideClick,
    isOpen,
    onCloseDialog,
    showOverlay = true,
    title,
    afterTitle,
    showHeader = true,
    noPadding,
    isModal = true,
    align = 'center',
    className,
    header,
    footer,
    disableOverflow = false,
    'data-test': dataTest = 'dialog',
}: AstraDialogProps) => {
    const { refs, context } = useFloating({
        open: isOpen,
        onOpenChange: open => {
            if (!open) onCloseDialog?.()
        },
    })

    const dismiss = useDismiss(context, {
        outsidePress: !disableOutsideClick,
        outsidePressEvent: 'pointerdown',
    })
    const role = useRole(context, { role: 'dialog' })
    const { getFloatingProps } = useInteractions([dismiss, role])

    // A custom header takes the bar; otherwise the title leads the body without a divider
    const showTitleInBody = showHeader && !header

    // Movement is what causes vestibular discomfort, so reduced motion drops the scale and keeps
    // the fade, which still signals the dialog arriving. The overlay is opacity-only either way.
    const shouldReduceMotion = useReducedMotion()
    const closedScale = shouldReduceMotion ? {} : { scale: 0.95 }
    const openScale = shouldReduceMotion ? {} : { scale: 1 }

    const { setScrollElement, fade } = useScrollFade(!disableOverflow)

    // Clipping keeps scrolling content inside the corner radius. disableOverflow exists so
    // absolutely positioned children can escape the content area, so it must not clip them.
    const surfaceClassName = astraTwMerge(
        'relative flex min-h-0 flex-col rounded-xl border bg-surface-base shadow-dialog',
        disableOverflow ? '' : 'overflow-hidden'
    )

    const contentClassName = astraTwMerge('min-h-0', noPadding ? 'p-0' : 'p-4', showTitleInBody ? 'pt-0' : '', disableOverflow ? '' : 'overflow-y-auto')

    return (
        <AnimatePresence>
            {isOpen && (
                <FloatingPortal>
                    {isModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, transition: { ease: 'easeIn', duration: 0.15 } }}
                            transition={{ ease: [0.32, 0.72, 0, 1], duration: 0.3 }}
                            className={astraTwMerge('fixed inset-0 z-elevated', showOverlay ? 'bg-black/25' : '')}
                        />
                    )}
                    <FloatingFocusManager context={context} modal={isModal}>
                        <motion.div
                            initial={{ opacity: shouldReduceMotion ? 0 : 0.5, ...closedScale }}
                            animate={{ opacity: 1, ...openScale }}
                            exit={{ opacity: 0, ...closedScale, transition: { ease: 'easeIn', duration: 0.15 } }}
                            transition={{ ease: [0.32, 0.72, 0, 1], duration: 0.3 }}
                            ref={refs.setFloating}
                            data-test={dataTest}
                            className={astraTwMerge(
                                'fixed z-elevated flex h-fit max-h-screen w-fit min-w-[24rem] flex-col',
                                align === 'top' ? 'inset-x-0 top-[15vh] mx-auto' : 'inset-0 m-auto',
                                className
                            )}
                            {...getFloatingProps({
                                'aria-describedby': undefined,
                                'aria-label': title,
                            })}
                        >
                            <div className={surfaceClassName}>
                                {!showTitleInBody && <h2 className="sr-only">{title}</h2>}
                                {showHeader &&
                                    (header ? (
                                        <div className="flex items-center gap-4 border-b border-subtle p-4 pr-12">{header}</div>
                                    ) : (
                                        <div className="flex items-center gap-4 p-4 pb-3 pr-12">
                                            {/* h2 is the dialog's main heading; h3 sizing keeps the title from dominating the body */}
                                            <AstraHeading as="h2" className="text-h3 font-medium leading-h3 tracking-h3">
                                                {title}
                                            </AstraHeading>
                                            {afterTitle}
                                        </div>
                                    ))}
                                <div className="relative flex min-h-0 flex-col">
                                    <div ref={setScrollElement} className={contentClassName}>
                                        {children}
                                    </div>
                                    {!disableOverflow && <ScrollFades fade={fade} />}
                                </div>
                                {footer && <div className="border-t border-subtle p-4">{footer}</div>}
                                {showHeader && (
                                    <div className="absolute right-2 top-2 z-elevated">
                                        <AstraButton
                                            onClick={onCloseDialog}
                                            icon={<AstraIcon icon={IconX} size="small" />}
                                            variant="ghost"
                                            data-test="dialog-close"
                                        />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </AnimatePresence>
    )
}
